import {
    Button,
    Chip,
    DataTable,
    DataTableCell,
    DataTableCustomComponent,
    DataTableRow,
    Modal,
    ModalBody,
    ModalFooter,
    Page,
    Spinner
} from '@7shifts/sous-chef';
import { __ } from 'i18n/translate';
import { Channel, Company } from 'adminPanel/types';
import { useCompanyChannels } from 'adminPanel/modules/Companies/hooks/useCompanyChannels';
import ChannelStatus from 'modules/Channels/components/ChannelStatus';
import { useEffect, useState } from 'react';
import { getCompanyPhoneQuality, getChannelLogs, WhatsappLog } from 'adminPanel/api/companies';
import { format, parseISO } from 'date-fns/esm';
import { handleError } from 'api/apiUtils';

type Props = {
    company: Company;
};

const QUALITY_COLOR: Record<string, string> = {
    GREEN: '#2f9e44',
    YELLOW: '#f59f00',
    RED: '#e03131',
    UNKNOWN: '#adb5bd'
};

const QUALITY_ICON: Record<string, string> = {
    GREEN: '●',
    YELLOW: '▲',
    RED: '●',
    UNKNOWN: '○'
};

const QUALITY_LABEL: Record<string, string> = {
    GREEN: 'Alta',
    YELLOW: 'Média',
    RED: 'Baixa',
    UNKNOWN: '—'
};

const LOG_TYPE_LABEL: Record<WhatsappLog['type'], string> = {
    CONNECTED: 'Conectado',
    DISCONNECTED: 'Desconectado',
    RECONNECTING: 'Reconectando',
    QR_GENERATED: 'QR gerado',
    QR_EXPIRED: 'QR expirado',
    ERROR: 'Erro'
};

const LOG_TYPE_COLOR: Record<WhatsappLog['type'], string> = {
    CONNECTED: '#2f9e44',
    DISCONNECTED: '#e03131',
    RECONNECTING: '#f59f00',
    QR_GENERATED: '#339af0',
    QR_EXPIRED: '#868e96',
    ERROR: '#e03131'
};

const CompanyChannelsPage = ({ company }: Props) => {
    const { channels, isLoading } = useCompanyChannels(company.id);
    const [quality, setQuality] = useState<Record<number, string>>({});
    const [logsModal, setLogsModal] = useState<Channel | null>(null);

    useEffect(() => {
        if (!channels.length) return;
        channels
            .filter((c) => c.oficial && c.status === 'CONNECTED')
            .forEach((ch) => {
                getCompanyPhoneQuality(company.id, ch.id)
                    .then((res) => {
                        setQuality((prev) => ({
                            ...prev,
                            [ch.id]: res.quality_rating ?? 'UNKNOWN'
                        }));
                    })
                    .catch(() => {
                        setQuality((prev) => ({
                            ...prev,
                            [ch.id]: 'UNKNOWN'
                        }));
                    });
            });
    }, [channels]);

    return (
        <Page
            title={__('adminpanel.channels')}
            breadcrumbs={{ label: __('adminpanel.companies'), url: '../../' }}
        >
            <DataTable
                items={channels}
                isLoading={isLoading}
                itemComponent={(props) => (
                    <ChannelRow
                        {...props}
                        quality={quality}
                        onViewLogs={setLogsModal}
                    />
                )}
                columns={[
                    { name: 'id', label: '#ID', size: 0.08 },
                    { name: 'name', label: __('adminpanel.channel') },
                    { name: 'number', label: __('queuesPage.phoneNumber') },
                    { name: 'isOfficial', label: __('queuesPage.official'), size: 0.3 },
                    { name: 'quality', label: 'Qualidade', size: 0.18 },
                    { name: 'status', label: 'Status', size: 0.18 },
                    { name: 'logs', label: 'Histórico', size: 0.15 }
                ]}
            />

            {logsModal && (
                <ChannelLogsModal
                    channel={logsModal}
                    companyId={company.id}
                    onClose={() => setLogsModal(null)}
                />
            )}
        </Page>
    );
};

type ChannelRowProps = DataTableCustomComponent<Channel> & {
    quality: Record<number, string>;
    onViewLogs: (ch: Channel) => void;
};

const ChannelRow = ({ item, quality, onViewLogs }: ChannelRowProps) => {
    const q = quality[item.id];
    const qColor = q ? (QUALITY_COLOR[q] ?? QUALITY_COLOR.UNKNOWN) : null;
    const qIcon = q ? (QUALITY_ICON[q] ?? QUALITY_ICON.UNKNOWN) : null;
    const qLabel = q ? (QUALITY_LABEL[q] ?? '—') : null;

    return (
        <DataTableRow>
            <DataTableCell columnIndex={0}>{item.id}</DataTableCell>
            <DataTableCell columnIndex={1}>{item.name}</DataTableCell>
            <DataTableCell columnIndex={2}>
                {item.actualNumber ?? '—'}
            </DataTableCell>
            <DataTableCell columnIndex={3}>
                {item.oficial ? (
                    <Chip theme="success">{__('main.yes')}</Chip>
                ) : (
                    <Chip theme="warning">{__('main.no')}</Chip>
                )}
            </DataTableCell>
            <DataTableCell columnIndex={4}>
                {item.oficial && item.status === 'CONNECTED' ? (
                    q ? (
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                fontWeight: 600,
                                fontSize: 13,
                                color: qColor ?? undefined
                            }}
                        >
                            <span style={{ fontSize: 10 }}>{qIcon}</span>
                            {qLabel}
                        </span>
                    ) : (
                        <span style={{ fontSize: 13, color: '#adb5bd' }}>
                            Carregando…
                        </span>
                    )
                ) : (
                    <span style={{ fontSize: 13, color: '#adb5bd' }}>—</span>
                )}
            </DataTableCell>
            <DataTableCell columnIndex={5}>
                <ChannelStatus status={item.status} />
            </DataTableCell>
            <DataTableCell columnIndex={6}>
                <Button
                    theme="hollow"
                    size="min-width-100"
                    onClick={() => onViewLogs(item)}
                >
                    Ver logs
                </Button>
            </DataTableCell>
        </DataTableRow>
    );
};

const ChannelLogsModal = ({
    channel,
    companyId,
    onClose
}: {
    channel: Channel;
    companyId: number;
    onClose: () => void;
}) => {
    const [logs, setLogs] = useState<WhatsappLog[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getChannelLogs(companyId, channel.id)
            .then(setLogs)
            .catch(handleError)
            .finally(() => setLoading(false));
    }, [channel.id, companyId]);

    return (
        <Modal
            header={`Histórico — ${channel.name}`}
            onClose={onClose}
        >
            <ModalBody>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                        <Spinner />
                    </div>
                ) : logs.length === 0 ? (
                    <p style={{ color: '#adb5bd', textAlign: 'center', padding: 32 }}>
                        Nenhum evento registrado para este canal.
                    </p>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: '#f8f9fa', textAlign: 'left' }}>
                                <th style={{ padding: '8px 12px' }}>Evento</th>
                                <th style={{ padding: '8px 12px' }}>Motivo</th>
                                <th style={{ padding: '8px 12px' }}>Cód.</th>
                                <th style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>Data/hora</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map((log) => (
                                <tr key={log.id} style={{ borderTop: '1px solid #dee2e6' }}>
                                    <td style={{ padding: '8px 12px' }}>
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                fontWeight: 600,
                                                color: LOG_TYPE_COLOR[log.type]
                                            }}
                                        >
                                            <span
                                                style={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    background: LOG_TYPE_COLOR[log.type],
                                                    flexShrink: 0,
                                                    display: 'inline-block'
                                                }}
                                            />
                                            {LOG_TYPE_LABEL[log.type]}
                                        </span>
                                    </td>
                                    <td style={{ padding: '8px 12px', color: '#495057', maxWidth: 320 }}>
                                        {log.reason ?? '—'}
                                    </td>
                                    <td style={{ padding: '8px 12px', color: '#868e96' }}>
                                        {log.statusCode ?? '—'}
                                    </td>
                                    <td style={{ padding: '8px 12px', color: '#868e96', whiteSpace: 'nowrap' }}>
                                        {format(parseISO(log.createdAt), 'dd/MM/yyyy HH:mm:ss')}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </ModalBody>
            <ModalFooter
                actions={{
                    secondary: (
                        <Button theme="hollow" onClick={onClose}>
                            Fechar
                        </Button>
                    )
                }}
            />
        </Modal>
    );
};

export default CompanyChannelsPage;
